import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/hooks/useAuth";
import { supabase, isSupabaseConfigured } from "@/lib/supabase";
import { useState, useEffect } from "react";
import { uploadToBucket } from "@/lib/storage";
import { SEOHead } from "@/components/SEOHead";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

const SellerShop = () => {
  const { user } = useAuth();
  const qc = useQueryClient();
  const { data: shop } = useQuery({
    queryKey: ["my-shop", user?.id],
    queryFn: async () => {
      if (!user) return null;
      const { data } = await supabase
        .from("shops")
        .select("id, name, handle, bio, logo_url, location")
        .eq("owner_id", user.id)
        .maybeSingle();
      return data;
    },
    enabled: !!user,
  });

  const [form, setForm] = useState({ name: "", handle: "", bio: "", logo_url: "", location: "" });
  const [uploading, setUploading] = useState(false);
  useEffect(() => {
    if (shop) setForm({
      name: shop.name ?? "",
      handle: shop.handle ?? "",
      bio: shop.bio ?? "",
      logo_url: shop.logo_url ?? "",
      location: shop.location ?? "",
    });
  }, [shop]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error("Not signed in");
      const payload = { ...form, owner_id: user.id } as any;
      if (shop?.id) {
        const { error } = await supabase
          .from("shops")
          .update(payload)
          .eq("id", shop.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("shops").insert(payload);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      toast.success("Shop saved");
      qc.invalidateQueries({ queryKey: ["my-shop", user?.id] });
    },
    onError: (err: any) => toast.error(err.message || "Failed to save shop"),
  });

  const onLogoChange = async (file?: File) => {
    if (!file || !user) return;
    setUploading(true);
    try {
      const ext = file.name.split(".").pop() || "png";
      const path = `shops/${user.id}/logo.${ext}`;
      const url = await uploadToBucket({ bucket: "shop_logos", path, file });
      setForm((f) => ({ ...f, logo_url: url }));
      toast.success("Logo uploaded");
    } catch (e: any) {
      console.warn("Logo upload failed", e);
      toast.error("Logo upload failed");
    } finally {
      setUploading(false);
    }
  };
  return (
    <main className="container mx-auto px-4 py-10">
      <SEOHead
        title="Shop Settings - Bahama Marketplace Seller"
        description="Configure your Bahama Marketplace shop settings, branding, and policies to attract more customers."
      />
      <h1 className="text-3xl font-heading font-bold mb-3">Your Shop Profile</h1>
      <p className="text-muted-foreground mb-6">Create or update your shop name, handle, bio, and logo.</p>
      <div className="rounded-xl border p-6 grid gap-4 max-w-xl">
        <div>
          <Label>Name</Label>
          <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
        </div>
        <div>
          <Label>Handle</Label>
          <Input value={form.handle} onChange={(e) => setForm((f) => ({ ...f, handle: e.target.value }))} />
        </div>
        <div>
          <Label>Bio</Label>
          <Input value={form.bio} onChange={(e) => setForm((f) => ({ ...f, bio: e.target.value }))} />
        </div>
        <div>
          <Label>Logo</Label>
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-lg bg-muted border flex items-center justify-center overflow-hidden">
              {form.logo_url ? (
                <img src={form.logo_url} alt="Logo" className="w-full h-full object-cover" />
              ) : (
                <span className="text-xs text-muted-foreground">No logo</span>
              )}
            </div>
            <Input type="file" accept="image/*" onChange={(e) => onLogoChange(e.target.files?.[0])} disabled={uploading} />
          </div>
        </div>
        <div>
          <Label>Location</Label>
          <Input value={form.location} onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))} />
        </div>
        <div className="mt-2 flex gap-3">
          <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending || uploading}>Save</Button>
        </div>
      </div>
    </main>
  );
};

export default SellerShop;